"use client";

import { useState } from "react";
import Image from "next/image";
import { useTenant } from "@/hooks/useTenant";

interface TenantLogoProps {
  /**
   * Tamanho do logo
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'custom';

  /**
   * Largura customizada (apenas quando size="custom")
   */
  width?: number;

  /**
   * Altura customizada (apenas quando size="custom")
   */
  height?: number;

  /**
   * Classes CSS adicionais
   */
  className?: string;

  /**
   * Texto alternativo customizado
   */
  alt?: string;

  /**
   * Se deve usar fallback para nome quando logo não carregar
   */
  showFallback?: boolean;

  /**
   * Prioridade de carregamento (para logos importantes)
   */
  priority?: boolean;

  /**
   * Variante do logo a ser usada
   */
  variant?: 'logo' | 'favicon' | 'apple-touch-icon';

  /**
   * Callback quando logo carrega com erro
   */
  onError?: () => void;

  /**
   * Callback quando logo carrega com sucesso
   */
  onLoad?: () => void;
}

/**
 * Componente para exibir logo do tenant de forma dinâmica
 * Inclui fallbacks automáticos e diferentes tamanhos
 */
export function TenantLogo({
  size = 'md',
  width,
  height,
  className = '',
  alt,
  showFallback = true,
  priority = false,
  variant = 'logo',
  onError,
  onLoad
}: TenantLogoProps) {
  const { tenant, isLoading } = useTenant();
  const [imageError, setImageError] = useState(false);

  // Definir dimensões baseadas no tamanho
  const dimensions = getDimensions(size, width, height);

  // Estado de loading
  if (isLoading) {
    return (
      <div
        className={`bg-gray-200 animate-pulse rounded ${className}`}
        style={{ width: dimensions.width, height: dimensions.height }}
      />
    );
  }

  // Tenant não encontrado
  if (!tenant) {
    return showFallback ? (
      <div
        className={`bg-gray-100 border border-gray-300 rounded flex items-center justify-center text-gray-500 font-medium ${className}`}
        style={{ width: dimensions.width, height: dimensions.height }}
      >
        Logo
      </div>
    ) : null;
  }

  // URLs dos diferentes assets
  const logoUrls = getLogoUrls(tenant, variant);

  // Text fallback
  const logoAlt = alt || `Logo ${tenant.nome}`;
  const fallbackText = getFallbackText(tenant.nome);

  // Erro de imagem ou URL não disponível
  if (imageError || !logoUrls.primary) {
    return showFallback ? (
      <div
        className={`bg-gradient-to-br from-blue-600 to-blue-700 rounded flex items-center justify-center text-white font-bold shadow-sm ${className}`}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          fontSize: `${Math.min(dimensions.width, dimensions.height) * 0.3}px`
        }}
        title={logoAlt}
      >
        {fallbackText}
      </div>
    ) : null;
  }

  const handleImageError = () => {
    setImageError(true);
    onError?.();
  };

  const handleImageLoad = () => {
    onLoad?.();
  };

  return (
    <div className={className} style={{ width: dimensions.width, height: dimensions.height }}>
      <Image
        src={logoUrls.primary}
        alt={logoAlt}
        width={dimensions.width}
        height={dimensions.height}
        priority={priority}
        className="object-contain w-full h-full"
        onError={handleImageError}
        onLoad={handleImageLoad}
        quality={75}
        sizes={`${dimensions.width}px`}
        unoptimized={logoUrls.primary?.startsWith('/api/v1/assets/')}
      />
    </div>
  );
}

/**
 * Obtém as dimensões baseadas no tamanho
 */
function getDimensions(size: string, customWidth?: number, customHeight?: number) {
  if (size === 'custom' && customWidth && customHeight) {
    return { width: customWidth, height: customHeight };
  }

  const sizeMap = {
    xs: { width: 24, height: 24 },
    sm: { width: 32, height: 32 },
    md: { width: 48, height: 48 },
    lg: { width: 64, height: 64 },
    xl: { width: 96, height: 96 }
  };

  return sizeMap[size as keyof typeof sizeMap] || sizeMap.md;
}

/**
 * Obtém URLs do logo baseado no tenant e variante
 */
function getLogoUrls(tenant: any, variant: string) {
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://porttal360.com.br';
  const config = tenant.configurations;

  // 🔄 SISTEMA HÍBRIDO: Priorizar slug se disponível
  const identifier = tenant.slug || tenant.id;

  // URLs do sistema novo (dinâmico) - usar slug se disponível
  const dynamicUrls = {
    logo: `/api/v1/assets/${identifier}/logo.png`,
    favicon: `/api/v1/assets/${identifier}/favicon.ico`,
    'apple-touch-icon': `/api/v1/assets/${identifier}/apple-touch-icon.png`
  };

  // URLs do sistema antigo (estático) - fallback
  const staticUrls = {
    logo: config?.logo_url,
    favicon: config?.favicon_url,
    'apple-touch-icon': config?.favicon_url // Fallback para favicon se não tiver apple icon
  };

  const primary = staticUrls[variant as keyof typeof staticUrls] ||
                  dynamicUrls[variant as keyof typeof dynamicUrls];

  const fallback = variant === 'logo' ?
    `${siteUrl}/api/v1/assets/defaults/logo.png` :
    `${siteUrl}/api/v1/assets/defaults/${variant === 'apple-touch-icon' ? 'apple-touch-icon.png' : 'favicon.ico'}`;

  return { primary, fallback };
}

/**
 * Gera texto de fallback baseado no nome do tenant
 */
function getFallbackText(nome: string): string {
  if (!nome) return 'L';

  // Tentar usar iniciais das palavras principais
  const words = nome.split(' ').filter(word => word.length > 2);

  if (words.length >= 2) {
    return words.slice(0, 2).map(word => word[0]).join('').toUpperCase();
  }

  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }

  // Fallback para primeiras 2 letras
  return nome.substring(0, 2).toUpperCase();
}

/**
 * Componente específico para header/navbar
 */
export function TenantLogoHeader(props: Omit<TenantLogoProps, 'size'>) {
  return <TenantLogo size="lg" priority={true} {...props} />;
}

/**
 * Componente específico para favicon
 */
export function TenantFavicon(props: Omit<TenantLogoProps, 'variant' | 'size'>) {
  return <TenantLogo variant="favicon" size="sm" {...props} />;
}

/**
 * Componente específico para cards/listagens
 */
export function TenantLogoCard(props: Omit<TenantLogoProps, 'size'>) {
  return <TenantLogo size="md" {...props} />;
}